/*
 * Copyright (c) 2021 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain proprietary information of OCLC Online Computer
 * Library Center, Inc. and shall not be disclosed in whole or in any part to any third party or used by any person
 * for any purpose, without written consent of OCLC, Inc.  Duplication of any portion of these materials shall include
 * this notice.
 */
import React, { useState, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import { makeStyles } from '@material-ui/core/styles'
import MuiLink from '@material-ui/core/Link'
import {
  Box,
  Card,
  CardHeader,
  Typography,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core'
import AlertNotification from './AlertNotification'
import { createAccount } from '../services/AuthenticationService'
import useBreakpoints from '../hooks/useBreakpoints'
import SuccessCard from './SuccessCard'

const useCustomStyles = makeStyles((theme) => ({
  accountButton: {
    height: 44,
    width: 142,
    textTransform: 'none',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      width: 152,
    },
  },
  accountHeader: {
    fontSize: '1.75rem',
    lineHeight: '20px',
    color: theme.palette.bluePrimary.main,
    fontFamily: theme.typography.fontFamily,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  alertBox: {
    padding: theme.spacing(3, 4, 0),
    [theme.breakpoints.down('xs')]: {
      width: 360,
      margin: 'auto',
      padding: theme.spacing(3, 2.5, 0),
    },
  },
  buttonBox: {
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(3),
    },
  },
  cancelButton: {
    height: 44,
    width: 142,
    textTransform: 'none',
    [theme.breakpoints.down('xs')]: {
      width: 152,
    },
  },
  cardContent: {
    textAlign: 'left',
    justifyContent: 'center',
    display: 'flex',
    padding: theme.spacing(5, 0, 3.75),
    '&.MuiCardContent-root:last-child': {
      paddingBottom: theme.spacing(4),
    },
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
  cardFormColumn: {
    width: 360,
    padding: theme.spacing(0, 3.5, 0, 3.5),
    borderLeft: `1px solid ${theme.palette.modalGrayBorder}`,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2.5),
      borderLeft: 'none',
      margin: 'auto',
    },
  },
  cardHeader: {
    background: theme.palette.modalHeader,
    borderBottom: `1px solid ${theme.palette.modalGrayBorder}`,
    height: 82,
  },
  cardHolder: {
    width: '684px',
    padding: theme.spacing(6, 0),
    margin: 'auto',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      width: '100%',
      minWidth: 0,
      padding: 0,
      '& .MuiPaper-rounded': {
        borderRadius: 0,
      },
    },
  },
  cardTextColumn: {
    width: 325,
    padding: theme.spacing(0, 3.25, 0, 4.875),
    [theme.breakpoints.down('xs')]: {
      width: 360,
      padding: theme.spacing(0, 2.5),
      margin: 'auto',
    },
  },
  checkbox: {
    '& .MuiIconButton-root:focus-within': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  formHeader: {
    marginBottom: theme.spacing(2),
    width: '296px',
  },
  inputTextField: {
    width: 292,
    [theme.breakpoints.down('xs')]: {
      width: 320,
    },
  },
  inputTextFieldError: {
    width: 292,
    '& .MuiOutlinedInput-root': {
      border: `2px solid ${theme.palette.notificationError.main}`,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    [theme.breakpoints.down('xs')]: {
      width: 320,
    },
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  privacyAcknowledgement: {
    paddingTop: theme.spacing(2),
  },
  boldText: {
    fontWeight: 'bold',
  },
  privacyNotice: {
    fontWeight: 'bold',
    [theme.breakpoints.down('xs')]: {
      paddingTop: theme.spacing(3),
      borderTop: `1px solid ${theme.palette.modalGrayBorder}`,
    },
  },
  privacyNoticeMessage: {
    '& a': {
      color: theme.palette.bluePrimary.main,
    },
  },
}))

/**
 * The Create Account Card.
 * @returns {Node} - A react Node.
 */
function CreateAccountCard() {
  const classes = useCustomStyles()
  const intl = useIntl()
  const usernameRef = useRef()
  const passwordRef = useRef()
  const confirmPasswordRef = useRef()
  const emailRef = useRef()
  const captchaRef = useRef()
  const { isXs } = useBreakpoints()
  const hostname = window && window.location && window.location.hostname
  const devDomains = RegExp(/(\.(dev|nit|nat)\.oclc\.org)|(localhost)/, 'g')

  const [captchaState] = useState({
    recaptchaSiteKey: devDomains.test(hostname)
      ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
      : '6LctDCscAAAAAIZLHotM7I8VkLdIRDll5WPsTSRw',
  })

  const [values, setValues] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    token: '',
    showPassword: false,
  })
  const [checkBoxState, setCheckboxState] = useState(false)
  const [errors, setErrors] = useState({
    password: false,
    username: false,
    confirmPassword: false,
    email: false,
    age: false,
    token: false,
  })
  const [showAlerts, setShowAlerts] = useState({
    retry: false,
    badPassword: false,
    badPasswordMatch: false,
    badEmail: false,
    usernameInUse: false,
    emailInUse: false,
  })
  const [createAccountSuccess, setCreateAccountSuccess] = useState(false)

  const privacyMessage = intl.formatMessage({ id: 'privacy_policy', defaultMessage: 'Privacy Policy' })
  const privacyPolicyLink = (
    <MuiLink
      data-testid='privacy-policy-link'
      target='_blank'
      href={'https://policies.oclc.org/en/privacy/privacy-statement.html'}>
      {privacyMessage}
    </MuiLink>
  )
  const forgotPageMessage = intl.formatMessage({
    id: 'forgot_account_info_link',
    defaultMessage: 'Forgot username or password page',
  })
  const forgotPageLink = (
    <MuiLink data-testid='email-in-use-link' target='_blank' href='/forgot'>
      {forgotPageMessage}
    </MuiLink>
  )

  // prettier-ignore
  const messages = {
    createAccount: intl.formatMessage({id: 'create_account', defaultMessage: 'Create account'}),
    ageCheckbox: intl.formatMessage({id: 'age_check', defaultMessage: 'I certify I am at least 16 years of age'}),
    privacyAcknowledgement: intl.formatMessage({id: 'privacy_acknowledgement', defaultMessage: 'By creating an account you acknowledge that you have read and understand the Privacy Policy and agree to the Terms of Use.'}),
    privacyNotice: intl.formatMessage({id: 'privacy_notice', defaultMessage: 'Privacy Notice'}),
    privacyStatement: intl.formatMessage({id: 'privacy_statement', defaultMessage: 'When you create an account, OCLC collects the personal data you provide and processes it in the United States of America. OCLC will use your personal data only for the purpose of providing services (and related services that use shared account information, including CONTENTdm and Digital Collection Gateway, to the extent you use those services). For more information on how your personal data is used, disclosed, and secured, please review OCLC’s {privacy_policy}. Please contact a staff member at your institution for more information. By creating an account you acknowledge that you have read and understand the Privacy Policy and agree to the Terms of Use.'}, {privacy_policy: privacyPolicyLink}),
    emailLabel: intl.formatMessage({id: 'email_address', defaultMessage: 'E-mail address'}),
    cancelMessage: intl.formatMessage({id: 'cancel', defaultMessage: 'Cancel'}),
    usernameLabel: intl.formatMessage({id: 'username', defaultMessage: 'Username'}),
    passwordLabel: intl.formatMessage({id: 'password', defaultMessage: 'Password'}),
    createAccountReqs: intl.formatMessage({id: 'create_account_reqs', defaultMessage: 'All fields are required to create an account.'}),
    createAccountRules: intl.formatMessage({id: 'create_account_rules', defaultMessage: 'Your username cannot be changed once created. Passwords must be letters or numbers only.'}, {username: 'username', passwords: 'Passwords', b: (word)=> <b>{word}</b>}),
    confirmPasswordLabel: intl.formatMessage({id: 'retype_password', defaultMessage: 'Re-type password'}),
    enterEmail: intl.formatMessage({id: 'error_no_email', defaultMessage: 'Enter an email address for your account.'}),
    enterPassword: intl.formatMessage({id: 'error_no_password', defaultMessage: 'Enter a password for your account.'}),
    enterRetypePassword: intl.formatMessage({id: 'error_no_retype_password', defaultMessage: 'Enter your Password again in the Re-type Password field to verify your password.'}),
    enterUsername: intl.formatMessage({id: 'error_no_username', defaultMessage: 'Enter a username for your account.'}),
    enterAge: intl.formatMessage({id: 'error_no_age', defaultMessage: 'Indicate that you are at least 16 years of age using the checkbox.'}),
    enterRecaptcha: intl.formatMessage({id: 'error_no_recaptcha', defaultMessage: 'Verify that you are not a robot.'}),
    retry: intl.formatMessage({id: 'error_retry', defaultMessage: 'Try again. A system error occurred.'}),
    login: intl.formatMessage({id: 'success_login', defaultMessage: 'Login.'}),
    successThankYou: intl.formatMessage({id: 'success_thanks', defaultMessage: 'Thank you for registering!'}),
    successReceiveEmail: intl.formatMessage({id: 'success_email', defaultMessage: 'You will receive a confirmation e-mail with information about your account.'}),
    successInstructions: intl.formatMessage({id: 'success_instructions', defaultMessage: 'Your account is active to use with OCLC products.\n\nYou can login to change your password.\n'}),
    badPassword: intl.formatMessage({id: 'bad_password_alert', defaultMessage: 'Password contains invalid characters. Use numbers and letters for your password.'}),
    badPasswordMatch: intl.formatMessage({id: 'bad_password_match_alert', defaultMessage: 'Your password entries did not match. Try again.'}),
    badEmail: intl.formatMessage({id: 'bad_email_alert', defaultMessage: 'The E-mail Address you entered is not in a valid format. Re-enter your E-mail Address.'}),
    usernameInUse: intl.formatMessage({id: 'username_in_use_alert', defaultMessage: 'The user name you entered is unavailable. Enter a different user name'}),
    emailInUse: intl.formatMessage({id: 'email_in_use_alert', defaultMessage: 'The E-mail Address you entered is already registered. If you have forgotten your username or password, navigate to {forgot_page}.'}, {forgot_page: forgotPageLink}),
  }
  const cardHeaderTitle = (
    <Typography
      variant='h1'
      data-testid='create-account-card-header'
      underline='none'
      className={classes.accountHeader}>
      {messages.createAccount}
    </Typography>
  )

  const verifyRecaptcha = async (response) => {
    setValues({ ...values, token: response })
    setErrors({ ...errors, token: false })
  }

  const clearForm = () => {
    setValues({ username: '', password: '', confirmPassword: '', email: '', token: '', showPassword: false })
    usernameRef.current.value = ''
    passwordRef.current.value = ''
    confirmPasswordRef.current.value = ''
    emailRef.current.value = ''
    captchaRef.current.reset()
    setCheckboxState(false)
  }

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault()
      setShowAlerts({
        retry: false,
        badPassword: false,
        badPasswordMatch: false,
        badEmail: false,
        usernameInUse: false,
        emailInUse: false,
      })
      if (!values.token) setErrors({ ...errors, token: true })
      if (!checkBoxState) setErrors({ ...errors, age: true })
      if (!values.email) setErrors({ ...errors, email: true })
      if (!values.confirmPassword) setErrors({ ...errors, confirmPassword: true })
      if (!values.password) setErrors({ ...errors, password: true })
      if (!values.username) setErrors({ ...errors, username: true })

      if (
        values.password &&
        values.username &&
        values.confirmPassword &&
        values.email &&
        checkBoxState &&
        values.token &&
        isEmailValid(values.email) &&
        !isBasPassword(values.password) &&
        !isBasPassword(values.confirmPassword) &&
        values.password === values.confirmPassword
      ) {
        const response = await createAccount(values)
        if (response) {
          if (response.status === 200) {
            setCreateAccountSuccess(true)
          } else if (response.detail.includes('User ID')) {
            setShowAlerts({ ...showAlerts, emailInUse: false, usernameInUse: true })
            clearForm()
          } else if (response.detail.includes('Email')) {
            setShowAlerts({ ...showAlerts, emailInUse: true, usernameInUse: false })
            clearForm()
          } else {
            setShowAlerts({ ...showAlerts, retry: true })
            clearForm()
          }
        } else {
          setShowAlerts({ ...showAlerts, retry: true })
          clearForm()
        }
      } else {
        setShowAlerts({
          retry: false,
          usernameInUse: false,
          emailInUse: false,
          badPassword: isBasPassword(values.password) || isBasPassword(values.confirmPassword),
          badPasswordMatch: values.password !== values.confirmPassword,
          badEmail: values.email && !isEmailValid(values.email),
        })
        clearForm()
      }
    },
    [values.username, values.password, values.confirmPassword, values.email, checkBoxState, values.token],
  )

  const isEmailValid = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(email))
  }

  const isBasPassword = (password) => {
    return /[^A-Za-z0-9]/g.test(password)
  }

  const handleChange = (prop) => (event) => {
    setValues({ ...values, [prop]: event.target.value })
    if (event.target.value.length > 0) setErrors({ ...errors, [prop]: false })
  }

  const handleCheckboxChange = (event) => {
    setCheckboxState(event.target.checked)
    if (event.target.checked) setErrors({ ...errors, age: false })
  }

  const handleCancel = () => {
    clearForm()
    setShowAlerts({
      retry: false,
      badPassword: false,
      badPasswordMatch: false,
      badEmail: false,
      usernameInUse: false,
      emailInUse: false,
    })
    setErrors({ password: false, username: false, confirmPassword: false, email: false, age: false, token: false })
  }

  const createAlert = (testId, text) => {
    return (
      <Box className={classes.alertBox}>
        <AlertNotification variant='error' id={testId}>
          {text}
        </AlertNotification>
      </Box>
    )
  }

  const createInputError = (testId, text) => {
    return (
      <AlertNotification variant='error' id={testId} alertLevel='inline'>
        {text}
      </AlertNotification>
    )
  }

  const retryAlert = createAlert('account-retry-alert', messages.retry)
  const badPasswordAlert = createAlert('account-bad-password-alert', messages.badPassword)
  const badPasswordMatchAlert = createAlert('account-bad-password-match-alert', messages.badPasswordMatch)
  const badEmailAlert = createAlert('account-bad-email-alert', messages.badEmail)
  const usernameInUseAlert = createAlert('account-username-in-use-alert', messages.usernameInUse)
  const emailInUseAlert = createAlert('account-email-in-use-alert', messages.emailInUse)
  const activeAlerts = [
    showAlerts.retry ? retryAlert : null,
    showAlerts.badPassword ? badPasswordAlert : null,
    showAlerts.badPasswordMatch ? badPasswordMatchAlert : null,
    showAlerts.badEmail ? badEmailAlert : null,
    showAlerts.usernameInUse ? usernameInUseAlert : null,
    showAlerts.emailInUse ? emailInUseAlert : null,
  ]
  const noUserNameError = createInputError('no-username-error', messages.enterUsername)
  const noPasswordError = createInputError('no-password-error', messages.enterPassword)
  const noRetypePasswordError = createInputError('no-retype-password-error', messages.enterRetypePassword)
  const noEmailError = createInputError('no-email-error', messages.enterEmail)
  const noAgeError = createInputError('no-age-error', messages.enterAge)
  const noRecaptchaError = createInputError('no-recaptcha-error', messages.enterRecaptcha)

  const createAccountForm = (
    <>
      <form id='sign-in-form' data-testid='create-account-form'>
        <Box className={classes.formHeader}>
          <Typography className={classes.boldText}>{messages.createAccountReqs}</Typography>
          <Typography>{messages.createAccountRules}</Typography>
        </Box>
        <Box mb={2}>
          <Typography component='label' variant='body1' htmlFor='username-input' className={classes.label}>
            <strong>{messages.usernameLabel}</strong>
          </Typography>
          <TextField
            inputRef={usernameRef}
            className={errors.username ? classes.inputTextFieldError : classes.inputTextField}
            id='username-input'
            data-testid='username-input'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.username ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 20 }}
            onChange={handleChange('username')}
          />
          {errors.username ? noUserNameError : null}
        </Box>
        <Box mb={2}>
          <Typography component='label' variant='body1' htmlFor='pwd-input' className={classes.label}>
            <strong>{messages.passwordLabel}</strong>
          </Typography>
          <TextField
            inputRef={passwordRef}
            className={errors.password ? classes.inputTextFieldError : classes.inputTextField}
            id='pwd-input'
            data-testid='pwd-input'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.password ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 25 }}
            onChange={handleChange('password')}
            type={values.showPassword ? 'text' : 'password'}
          />
          {errors.password ? noPasswordError : null}
        </Box>
        <Box mb={2}>
          <Typography component='label' variant='body1' htmlFor='confirm-pwd-input' className={classes.label}>
            <strong>{messages.confirmPasswordLabel}</strong>
          </Typography>
          <TextField
            inputRef={confirmPasswordRef}
            className={errors.confirmPassword ? classes.inputTextFieldError : classes.inputTextField}
            id='confirm-pwd-input'
            data-testid='confirm-pwd-input'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.confirmPassword ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 25 }}
            onChange={handleChange('confirmPassword')}
            type={values.showPassword ? 'text' : 'password'}
          />
          {errors.confirmPassword ? noRetypePasswordError : null}
        </Box>
        <Box mb={1.5}>
          <Typography component='label' variant='body1' htmlFor='email-input' className={classes.label}>
            <strong>{messages.emailLabel}</strong>
          </Typography>
          <TextField
            inputRef={emailRef}
            className={errors.email ? classes.inputTextFieldError : classes.inputTextField}
            id='email-input'
            data-testid='email-input'
            size='small'
            variant='outlined'
            fullWidth
            color={errors.email ? 'secondary' : 'primary'}
            required
            defaultValue={''}
            inputProps={{ maxLength: 50 }}
            onChange={handleChange('email')}
            type='email'
          />
          {errors.email ? noEmailError : null}
        </Box>
        <Box mb={1.5}>
          <FormControlLabel
            data-testid='create-account-age-checkbox'
            className={classes.checkbox}
            control={
              <Checkbox
                inputProps={{ 'aria-label': 'I certify I am at least 16 years of age' }}
                color='default'
                required
                checked={checkBoxState}
                onChange={handleCheckboxChange}
                name='age'
              />
            }
            label={messages.ageCheckbox}
          />
          {errors.age ? noAgeError : null}
        </Box>
        <Box mb={2}>
          <ReCAPTCHA
            data-testid='create-account-recaptcha'
            ref={captchaRef}
            onChange={verifyRecaptcha}
            sitekey={captchaState.recaptchaSiteKey}
          />
          {errors.token ? noRecaptchaError : null}
        </Box>
        <Box className={classes.buttonBox}>
          <Button
            color='primary'
            variant='contained'
            className={classes.accountButton}
            type='submit'
            data-testid='create-account-button'
            onClick={submitForm}>
            {messages.createAccount}
          </Button>
          <Button
            color='primary'
            variant='outlined'
            className={classes.cancelButton}
            type='reset'
            value='Reset'
            data-testid='cancel-button'
            onClick={handleCancel}>
            {messages.cancelMessage}
          </Button>
        </Box>
      </form>
    </>
  )

  const cardTextColumn = (
    <div className={classes.cardTextColumn}>
      <Typography component='p' variant='caption' className={classes.privacyNotice}>
        {messages.privacyNotice}
      </Typography>
      <Typography component='p' variant='caption' className={classes.privacyNoticeMessage}>
        {messages.privacyStatement}
      </Typography>
      <Typography className={classes.privacyAcknowledgement} component='p' variant='caption'>
        {messages.privacyAcknowledgement}
      </Typography>
    </div>
  )

  const cardFormColumn = <div className={classes.cardFormColumn}>{createAccountForm}</div>

  const successCard = (
    <SuccessCard
      title={messages.createAccount}
      buttonText={messages.login}
      boldBodyText={messages.successThankYou}
      bodyText={messages.successInstructions}
    />
  )

  return createAccountSuccess ? (
    successCard
  ) : (
    <Box className={classes.cardHolder}>
      <Card elevation={9}>
        <CardHeader className={classes.cardHeader} title={cardHeaderTitle} />
        {activeAlerts.map((alert) => {
          if (alert) return <React.Fragment key={activeAlerts.indexOf(alert)}>{alert}</React.Fragment>
        })}
        <CardContent className={classes.cardContent}>
          {isXs ? cardFormColumn : cardTextColumn}
          {isXs ? cardTextColumn : cardFormColumn}
        </CardContent>
      </Card>
    </Box>
  )
}

export default CreateAccountCard
